import React, { useCallback, useEffect, useState } from 'react';
import { api, errText } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Lightbulb, Bug } from 'lucide-react';

const STATUSES = ['new', 'planned', 'in_progress', 'done', 'declined'];
const statusStyle = {
  new: 'bg-amber-500/10 text-amber-600 border-amber-500/40',
  planned: 'bg-primary/10 text-primary border-primary/30',
  in_progress: 'bg-primary/10 text-primary border-primary/30',
  done: '',
  declined: 'opacity-60',
};

/**
 * Where the report came from (ADR 0004 step 2). `page` is the matched route
 * PATTERN, never the URL — `/p/:token` as a URL would be a live magic link, and
 * backend/error_context.py withholds anything that is not pattern-shaped, which
 * is what `route-withheld` means when you see it.
 */
function FeedbackContext({ context }) {
  const c = context || {};
  const facts = [
    ['page', c.route], ['build', c.build], ['ref', c.correlation_id], ['viewport', c.viewport],
  ].filter(([, v]) => v);
  if (facts.length === 0 && !c.error_message) return null;
  return (
    <div className="space-y-1.5 border-t pt-2">
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-mono text-muted-foreground">
        {facts.map(([k, v]) => <span key={k}>{k}: {v}</span>)}
      </div>
      {c.error_message && (
        <pre className="text-[11px] font-mono whitespace-pre-wrap rounded bg-destructive/5 border border-destructive/20 px-2 py-1.5 text-destructive">
          {c.error_name ? `${c.error_name}: ` : ''}{c.error_message}
        </pre>
      )}
      {(c.component_stack || c.user_agent) && (
        <details className="text-[11px] text-muted-foreground">
          <summary className="cursor-pointer">Stack &amp; browser</summary>
          {c.user_agent && <p className="font-mono pt-1 break-all">{c.user_agent}</p>}
          {c.component_stack && <pre className="font-mono whitespace-pre-wrap pt-1">{c.component_stack}</pre>}
        </details>
      )}
    </div>
  );
}

export default function OwnerFeedback() {
  const [type, setType] = useState('all');
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    api.get('/owner/feedback', { params: { type } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (f, status) => {
    try {
      await api.patch(`/owner/feedback/${f.id}`, { status });
      toast.success(`Marked \"${f.title}\" as ${status.replace('_', ' ')}`);
      load();
    } catch (e) {
      toast.error(errText(e, 'Could not update'));
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4" data-testid="owner-feedback-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Feature requests & bug reports</h1>
        <p className="text-sm text-muted-foreground">What your customers need and what's broken — straight from their portals.</p>
      </div>
      <Tabs value={type} onValueChange={setType}>
        <TabsList data-testid="owner-feedback-filter">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="feature">Features</TabsTrigger>
          <TabsTrigger value="bug">Bugs</TabsTrigger>
        </TabsList>
      </Tabs>
      {!items ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="owner-feedback-empty">
          <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-40" /> Nothing here yet.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map((f) => (
            <Card key={f.id} data-testid={`owner-feedback-${f.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    {f.type === 'bug' ? <Bug className="h-4 w-4 text-destructive mt-0.5 shrink-0" /> : <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-medium">{f.title}</p>
                      <p className="text-xs text-muted-foreground">{f.created_by?.name} · {f.practice_name} · {(f.created_at || '').slice(0, 10)}{f.severity ? ` · severity: ${f.severity}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[10px] capitalize ${statusStyle[f.status] || ''}`}>{String(f.status).replace('_', ' ')}</Badge>
                    <Select value={f.status} onValueChange={(v) => setStatus(f, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs" data-testid={`owner-feedback-status-${f.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{f.message}</p>
                <FeedbackContext context={f.context} />
                {f.owner_note && <p className="text-xs rounded bg-secondary/60 border px-2 py-1.5">Owner note: {f.owner_note}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
